// router/index.ts
import { createRouter, createWebHistory } from "vue-router"

// New modular routing system
import { discoverRoutes } from '@/router/generator/discoverRoutes';
import { assembleRoutesFromDiscoveredModules } from '@/router/generator/assembleRoutes';

// Merge helper: combine two route trees by unique route name (recursively), preferring primary
function mergeRoutesByName(primary: any[], fallback: any[]): any[] {
  const byName = new Map<string, any>();
  const add = (route: any, source: 'primary' | 'fallback') => {
    const key = route && route.name ? String(route.name) : `__path__:${route.path}`;
    if (!byName.has(key)) {
      // Shallow copy to avoid side effects
      byName.set(key, { ...route });
    } else if (source === 'fallback') {
      // If fallback has children we don't have, merge them in
      const existing = byName.get(key);
      if (route.children && route.children.length > 0) {
        existing.children = mergeRoutesByName(existing.children || [], route.children);
      }
      byName.set(key, existing);
    }
  };

  // Add primary first
  primary.forEach(r => add(r, 'primary'));
  // Then merge fallback
  fallback.forEach(r => add(r, 'fallback'));

  // For routes present in both, merge children recursively
  const merged = Array.from(byName.values());
  merged.forEach(route => {
    const primaryMatch = primary.find(p => (p.name ? String(p.name) : `__path__:${p.path}`) === (route.name ? String(route.name) : `__path__:${route.path}`));
    const fallbackMatch = fallback.find(f => (f.name ? String(f.name) : `__path__:${f.path}`) === (route.name ? String(route.name) : `__path__:${route.path}`));
    if (primaryMatch && fallbackMatch) {
      if (primaryMatch.children || fallbackMatch.children) {
        route.children = mergeRoutesByName(primaryMatch.children || [], fallbackMatch.children || []);
      }
    }
  });

  return merged;
}
import { buildAppConfig, RoutingConfigBuilder } from '@/config/routing.config';
import { getProjectType, getProjectId, getSiteConfig } from '@/config/global-site-config';
import registerCrossAppHandoffInterceptor from '@/router/plugins/cross-app-handoff-interceptor';

/**
 * Early project context resolution for router creation
 * This ensures we create the correct routes from the start
 */
/**
 * Early project context resolution using the global site config system
 * This replaces all the complex detection logic with a single source of truth
 */
function resolveEarlyProjectContext(): { projectType: 'white_label' | 'private_label', projectId: string | null } {
  const projectType = getProjectType();
  const projectId = getProjectId();
  const siteConfig = getSiteConfig();
  
  if (siteConfig) {
    console.log('⚡ [Router] Using global site config (0ms):', { 
      projectType, 
      projectId: projectId || 'none',
      siteId: siteConfig.siteId
    });
  } else {
    console.log('⚡ [Router] Using global config fallback:', { 
      projectType, 
      projectId: projectId || 'none'
    });
  }
  
  return { projectType, projectId };
}

// Resolve project context before creating routes
const earlyContext = resolveEarlyProjectContext();
const projectType = earlyContext.projectType;
const initialProjectId = earlyContext.projectId;

const isDevelopment = import.meta.env.MODE === 'development';

// Log the resolved context for debugging  
if (isDevelopment) {
  console.log('🚀 [Router] Early context resolution complete:', {
    projectType,
    projectId: initialProjectId || 'none',
    currentPath: window.location.pathname
  });
}

// Export resolved context and external URLs
export { projectType, initialProjectId };
export const EXTERNAL_SHIRLY_LANDING_BASEURL = `${import.meta.env.VITE_SHIRLY_LANDING_URL}`;

// Create routes using the new modular system
const routingConfig = new RoutingConfigBuilder()
  .setProjectType(projectType)
  .setVariant('organizations') // Default variant - can be made dynamic
  .enableFeatureDetection(false)
  .setDevelopmentMode(isDevelopment)
  .build();

const appConfig = buildAppConfig(routingConfig, {});
let routes: any[] = [];

try {
  const { routes: discovered, errors, warnings } = discoverRoutes(true);
  if (errors.length > 0) {
    console.error('[Routing Discovery] Errors:', errors);
    if (import.meta.env.VITE_ROUTING_DISCOVERY_STRICT === 'true') {
      throw new Error('Routing discovery strict mode failed');
    }
  }
  if (warnings.length > 0) console.warn('[Routing Discovery] Warnings:', warnings);
  routes = assembleRoutesFromDiscoveredModules(discovered, appConfig);
} catch (e) {
  console.warn('⚠️ Routing discovery failed, using minimal fallback routes:', e);
  routes = [
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/error-pages/NotFoundView.vue'),
      meta: { isErrorPage: true }
    }
  ];
}

// Log routing system status in development
if (isDevelopment) {
  console.log('🚀 Using new modular routing system');
  console.log(`📋 Generated ${routes.length} routes for ${projectType} project type`);
  console.log('📦 App Configuration:', {
    variant: appConfig.variant,
    projectType: appConfig.projectType,
    enabledAppModules: Object.keys(appConfig.appModules).filter(key => 
      appConfig.appModules[key as keyof typeof appConfig.appModules]?.enabled
    ),
    enabledAdminModules: Object.keys(appConfig.adminModules).filter(key => 
      appConfig.adminModules[key as keyof typeof appConfig.adminModules]?.enabled
    )
  });
}

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  linkActiveClass: "active",
  linkExactActiveClass: "exact-active",
  routes,
});

registerCrossAppHandoffInterceptor(router);

// Router guard initialization is handled in main.ts to ensure proper timing
console.log('🛡️ Using new modular guard system - initialization handled in main.ts');

// Note: afterEach handlers are now managed by the SimplifiedRouterGuard
// to ensure proper ordering and state management

// Development-only router debugging
if (isDevelopment) {
  // Make router available globally for debugging
  (window as any).routerDebug = {
    router,
    routes,
    projectType,
    appConfig,
    routingConfig
  };
  
  console.log('🛠️ Router debug tools available at window.routerDebug');
}